// src/components/AlumniRegistrationTabs.js
import React, { useState } from "react";
import {
  Form,
  Button,
  Divider,
  Steps,
  Progress,
  message,
  Card,
  Typography,
  Modal,
  Row,
  Col,
  Space,
} from "antd";
import { useNavigate } from "react-router-dom";
import PersonalInfo from "./steps/PersonalInfo";
import AddressInfo from "./steps/AddressInfo";
import AcademicInfo from "./steps/AcademicInfo";
import ProfessionalInfo from "./steps/ProfessionalInfo";
import { PrinterOutlined } from "@ant-design/icons";

const { Step } = Steps;
const { Title, Text } = Typography;

export default function AlumniRegistrationTabs() {
  const [form] = Form.useForm();
  const navigate = useNavigate();

  const [current, setCurrent] = useState(0);
  const [photoList, setPhotoList] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmVisible, setConfirmVisible] = useState(false);

  const steps = ["Personal", "Address", "Academic", "Professional", "Review"];

  const stepFields = {
    0: ["first_name", "last_name", "roll_number", "dob", "gender", "email", "phone"],
    1: ["city", "state", "pin_code", "country", "address"],
    2: ["academic_records"],
    3: ["linked_in", "current_employer", "job_title", "industry", "workplace_country"],
    4: [],
  };

  const validateStep = async (index) => {
    if (index === 3 && (!photoList || photoList.length === 0)) {
      message.error("Please upload a profile photo before continuing.");
      return false;
    }

    const names = stepFields[index] || [];
    if (!names.length) return true;

    try {
      if (names.includes("academic_records")) {
        await form.validateFields();
      } else {
        await form.validateFields(names);
      }
      return true;
    } catch (err) {
      return false;
    }
  };

  const goto = async (i) => {
    if (i > current) {
      const ok = await validateStep(current);
      if (!ok) {
        message.error("Please fix validation errors before moving forward.");
        return;
      }
    }
    setCurrent(i);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const next = async () => {
    const ok = await validateStep(current);
    if (!ok) {
      message.error("Please fix validation errors before continuing.");
      return;
    }
    setCurrent((c) => Math.min(c + 1, steps.length - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const prev = () => {
    setCurrent((c) => Math.max(c - 1, 0));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const percent = Math.round((current / (steps.length - 1)) * 100);

  const renderReview = () => {
    const values = form.getFieldsValue(true);
    const photoUrl =
      photoList && photoList.length > 0 && photoList[0].originFileObj
        ? URL.createObjectURL(photoList[0].originFileObj)
        : values.profile_photo_url || null;

    const row = (label, val) => (
      <div className="review-row" key={label} style={{ display: "flex", gap: 12, padding: "8px 0", borderBottom: "1px dashed #f0f0f0" }}>
        <div className="review-row-label" style={{ minWidth: 140, fontWeight: 600, color: "#111827" }}>{label}</div>
        <div className="review-row-value" style={{ color: "#374151" }}>{val || "-"}</div>
      </div>
    );

    return (
      <div className="review-wrap" style={{ position: "relative", padding: 12 }}>
        <div className="watermark" style={{ position: "absolute", right: 16, top: 16, opacity: 0.04, fontSize: 56, fontWeight: 800, pointerEvents: "none" }}>PSG IAS</div>

        <Card className="review-card" bordered={false} style={{ borderRadius: 12 }}>
          <Title level={4}>Application Summary</Title>

          <Row gutter={[24, 24]}>
            <Col xs={24} md={16}>
              <div className="review-fields" style={{ paddingRight: 8 }}>
                {row("Name", `${values.first_name || ""} ${values.last_name || ""}`)}
                {row("Roll number", values.roll_number)}
                {row("DOB", values.dob ? values.dob.format("YYYY-MM-DD") : "")}
                {row("Gender", values.gender)}
                {row("Email", values.email)}
                {row("Phone", values.phone)}
                <Divider />
                {row("City", values.city)}
                {row("State", values.state)}
                {row("Pin", values.pin_code)}
                {row("Country", values.country)}
                {row("Address", values.address)}
              </div>
            </Col>

            <Col xs={24} md={8} className="review-side">
              <div style={{ display: "flex", flexDirection: "column", gap: 12, alignItems: "center" }}>
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt="profile"
                    className="review-photo"
                    style={{ width: 180, height: 180, objectFit: "cover", borderRadius: 12, border: "1px solid #eef2f4" }}
                  />
                ) : (
                  <div className="review-photo empty" style={{ width: 180, height: 180, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 12, background: "#fafafa", color: "#9aa4b2", border: "1px dashed #eee" }}>No photo</div>
                )}

                <div style={{ width: "100%" }}>
                  <Button icon={<PrinterOutlined />} block>
                    Print
                  </Button>
                </div>
              </div>
            </Col>
          </Row>

          <Divider />
          <Title level={5}>Academic Records</Title>
          {(values.academic_records || []).map((r, i) => (
            <Card size="small" key={i} className="review-academic-card" style={{ marginBottom: 12, borderRadius: 8 }}>
              <div className="review-academic-row"><strong>Record #{i + 1}</strong></div>
              <div>Level: {r.level}</div>
              <div>Degree: {r.degree}</div>
              <div>Major: {r.major}</div>
              <div>University: {r.university_name}</div>
            </Card>
          ))}
        </Card>
      </div>
    );
  };

  const openConfirm = async () => {
    const ok = await validateStep(current);
    if (!ok) {
      message.error("Please fix validation errors before submitting.");
      return;
    }
    if (!photoList || photoList.length === 0) {
      message.error("Please upload profile photo before submitting.");
      return;
    }
    setConfirmVisible(true);
  };

  const doSubmit = async () => {
    setConfirmVisible(false);
    if (submitting) return;
    setSubmitting(true);

    try {
      await form.validateFields();
      const values = form.getFieldsValue(true);
      const fd = new FormData();

      const keys = [
        "first_name",
        "last_name",
        "roll_number",
        "dob",
        "gender",
        "email",
        "phone",
        "city",
        "state",
        "pin_code",
        "country",
        "address",
        "linked_in",
        "current_employer",
        "job_title",
        "industry",
        "workplace_country",
      ];

      keys.forEach((k) => {
        const v = values[k];
        if (v != null) {
          if (k === "dob" && v && v.format) fd.append("dob", v.format("YYYY-MM-DD"));
          else fd.append(k, String(v));
        }
      });

      if (Array.isArray(values.academic_records)) {
        fd.append("academic_records", JSON.stringify(values.academic_records));
      } else {
        fd.append("academic_records", JSON.stringify([]));
      }

      if (photoList && photoList[0] && photoList[0].originFileObj) {
        fd.append("profile_photo", photoList[0].originFileObj);
      }

      const apiBase = (
  window.__APP_CONFIG__?.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost:8000"
).replace(/\/$/, ""); // remove trailing slash if any
      const endpoint = `${apiBase}/register.php`;

      const res = await fetch(endpoint, {
        method: "POST",
        body: fd,
      });

      const raw = await res.text();
      let data = {};
      try {
        data = raw ? JSON.parse(raw) : {};
      } catch (e) {
        throw new Error(raw || "Unexpected server response");
      }

      if (!res.ok) {
        const err = data.error || data.message || JSON.stringify(data);
        throw new Error(err);
      }

      // Success path: persist first name to sessionStorage and navigate to confirmation page
      message.success("Registration submitted — profile under review.");
      const firstName = (values && (values.first_name || values.firstName)) || "";
      // persist small fallback so confirmation page survives refresh
      try {
        sessionStorage.setItem("profileSubmittedFirstName", firstName);
      } catch (e) {
        // ignore storage errors
      }

      // reset local form state
      form.resetFields();
      setPhotoList([]);
      setCurrent(0);

      // navigate to profile-submitted page with state
      navigate("/profile-submitted", { state: { firstName } });
    } catch (err) {
      console.error("submit error:", err);
      message.error(err?.message || "Submission failed — check console.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="registration-shell" style={{ padding: 24 }}>
      {/* Basic layout spacing styles */}
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Header with logo + title */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 16,
            padding: "12px 8px",
            background: "#fff",
            borderRadius: 8,
            marginBottom: 18,
            border: "1px solid #eef2f4",
          }}
        >
          <a href="/" aria-label="Home" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none" }}>
            <div style={{ width: 56, height: 56, borderRadius: 8, background: "#f3f4f6", display: "flex", alignItems: "center", justifyContent: "center", color: "#6D28D9", fontWeight: 800, fontSize: 20 }}>
              PSG
            </div>
          </a>

          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 18, fontWeight: 700, color: "#111827" }}>PSG IAS Alumni Registration</div>
            <div style={{ color: "#6b7280", fontSize: 13 }}>Please complete the form to register as an alumnus.</div>
          </div>

          <div style={{ textAlign: "right", minWidth: 120 }}>
            <div style={{ fontSize: 12, color: "#6b7280" }}>Need help?</div>
            <div style={{ marginTop: 6 }}><a href="/contact">Contact us</a></div>
          </div>
        </div>

        <Row gutter={[24, 24]}>
          {/* Left pane: Steps + Progress (sticky on wide screens) */}
          <Col xs={24} md={7}>
            <div style={{ position: "relative" }}>
              <Card
                className="steps-card"
                style={{
                  padding: 16,
                  borderRadius: 12,
                  minHeight: 220,
                  boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
                }}
              >
                <div className="steps-header" style={{ marginBottom: 12 }}>
                  <Title level={4} className="steps-title" style={{ margin: 0 }}>Registration</Title>
                  <Text type="secondary">Complete these steps to register</Text>
                </div>

                <div className="steps-body" style={{ marginTop: 12 }}>
                  <div
                    style={{
                      // sticky behaviour on md+ screens
                      position: "sticky",
                      top: 24,
                      alignSelf: "flex-start",
                    }}
                  >
                    <Steps
                      current={current}
                      direction="vertical"
                      size="small"
                      onChange={goto}
                      className="vertical-steps"
                    >
                      {steps.map((s) => (
                        <Step key={s} title={s} />
                      ))}
                    </Steps>
                  </div>
                </div>

                <Divider />

                <div className="steps-footer" style={{ marginTop: 8 }}>
                  <Progress percent={percent} size="small" />
                  <div className="steps-footer-actions" style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ minHeight: 36 }}>
                      <Space>
                        {current > 0 && (
                          <Button onClick={prev} disabled={submitting}>
                            Back
                          </Button>
                        )}
                        {current < steps.length - 1 && (
                          <Button type="primary" onClick={next} disabled={submitting}>
                            Continue
                          </Button>
                        )}
                        {current === steps.length - 1 && (
                          <Button type="primary" onClick={openConfirm} loading={submitting}>
                            Submit
                          </Button>
                        )}
                      </Space>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </Col>

          {/* Right pane: Form area */}
          <Col xs={24} md={17}>
            <Card
              className="form-card"
              style={{
                padding: 16,
                borderRadius: 12,
                minHeight: 480,
                boxShadow: "0 8px 24px rgba(15,23,42,0.04)",
              }}
            >
              <Form form={form} layout="vertical">
                {current === 0 && <PersonalInfo />}
                {current === 1 && <AddressInfo />}
                {current === 2 && <AcademicInfo />}
                {current === 3 && <ProfessionalInfo photoList={photoList} setPhotoList={setPhotoList} />}
                {current === 4 && renderReview()}
              </Form>
            </Card>
          </Col>
        </Row>

        {/* Confirmation modal */}
        <Modal
          title="Confirm submission"
          visible={confirmVisible}
          onOk={doSubmit}
          onCancel={() => setConfirmVisible(false)}
          okText="Confirm & Submit"
          confirmLoading={submitting}
        >
          <p>All fields are mandatory. After submission admin will review your profile and you will not be able to edit until approval.</p>
          <p style={{ fontWeight: 600 }}>Proceed?</p>
        </Modal>
      </div>
    </div>
  );
}
